import React, {
  RefObject,
  SyntheticEvent,
  useCallback,
} from 'react';
import clsx from 'clsx';
import Autocomplete, {
  AutocompleteRenderInputParams,
  AutocompleteProps,
} from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import {
  InputProps as StandardInputProps,
} from '@mui/material/Input';
import SearchIcon from 'core/icons/search';

export type Props<T> = Pick<
  AutocompleteProps<T, boolean, boolean, boolean>,
  | 'className'
  | 'value'
  | 'defaultValue'
  | 'options'
  | 'renderOption'
  | 'disabled'
  | 'multiple'
  | 'autoComplete'
  | 'loading'
  | 'loadingText'
  | 'openOnFocus'
  | 'includeInputInList'
  | 'filterSelectedOptions'
  | 'noOptionsText'
  | 'blurOnSelect'
  | 'popupIcon'
  | 'freeSolo'
  | 'open'
  | 'clearOnBlur'
  | 'disablePortal'
  | 'onBlur'
  | 'onFocus'
  | 'onOpen'
  | 'onClose'
  | 'onInputChange'
  | 'filterOptions'
  | 'isOptionEqualToValue'
  | 'selectOnFocus'
  | 'classes'
  | 'inputValue'
  | 'groupBy'
  | 'renderGroup'
  | 'clearIcon'
  | 'onHighlightChange'
  | 'onKeyDown'
> & {
  renderTags?: any;
  ListboxComponent?: any;
  ListboxProps?: any;
  id?: string;
  label?: string;
  required?: boolean;
  getLabel: (option: T) => string;
  inputRef?: RefObject<any>;
  readonly?: boolean;
  dropdownClassName?: string;
  InputProps?: Partial<StandardInputProps>;
  onChange: (value: T | T[]) => void;
  disableRemove?: boolean;
  getOptionDisabled?: (option: T) => boolean;
  placeholder?: string;
} & Pick<StandardInputProps, 'inputProps'>;

export default function AutoCompletedInput<T>({
  id,
  className,
  label,
  required,
  getLabel,
  inputRef,
  classes,
  readonly,
  dropdownClassName,
  InputProps = {},
  onChange,
  options = [],
  filterSelectedOptions = true,
  autoComplete = false,
  includeInputInList = false,
  loading = false,
  clearOnBlur = true,
  openOnFocus = false,
  loadingText = 'Loading...',
  noOptionsText = 'No Options',
  renderTags: _renderTags,
  ListboxComponent,
  ListboxProps,
  popupIcon,
  value,
  placeholder,
  disabled,
  disableRemove = false,
  getOptionDisabled,
  clearIcon = null,
  inputProps,
  ...props
}: Props<T>) {
  const autoCompleteClasses = clsx('populate-autocomplete w-full', className, { disabled });
  const handleChange = (event: SyntheticEvent, value: any, reason: string) => {
    // when values are handled and displayed outside of mui input,
    // disable removing with backspace | delete
    if (reason !== 'removeOption' || !disableRemove) {
      onChange(value);
    }
  };

  const renderTags = _renderTags ?? useCallback((value: T[]) => (
    value.map((v: T) => (
      <span
        key={getLabel(v)}
        className="mr-1"
      >
        {getLabel(v)}
      </span>
    ))
  ), [getLabel]);

  const icon = typeof popupIcon === 'undefined' ? <SearchIcon className="search-icon" /> : popupIcon;
  return (
    <Autocomplete
      {...props}
      autoHighlight
      id={id}
      disabled={disabled}
      className={autoCompleteClasses}
      clearIcon={clearIcon}
      onChange={handleChange}
      getOptionDisabled={getOptionDisabled}
      value={value}
      options={options}
      getOptionLabel={(option: any) => getLabel(option as T)}
      popupIcon={!readonly && icon}
      renderValue={_renderTags ? ((selectedValue: any, getItemProps: any) => (
        renderTags(
          Array.isArray(selectedValue) ? selectedValue : [selectedValue],
          ({ index }: { index: number }) => getItemProps({ index }),
        )
      )) : undefined}
      loadingText={loadingText}
      noOptionsText={noOptionsText}
      classes={classes}
      readOnly={readonly}
      slots={{
        listbox: ListboxComponent,
      }}
      slotProps={{
        paper: { className: dropdownClassName ?? '' },
        listbox: ListboxProps,
      }}
      clearOnBlur={clearOnBlur}
      openOnFocus={openOnFocus}
      filterSelectedOptions={filterSelectedOptions}
      renderInput={(params: AutocompleteRenderInputParams) => (
        <TextField
          id={id}
          className="w-full"
          inputRef={inputRef}
          label={label}
          required={required}
          placeholder={placeholder}
          size="small"
          sx={{
            '& .MuiOutlinedInput-root': {
              height: '40px',
              fontSize: '0.875rem',
            },
            '& .MuiInputBase-input': {
              padding: '8px 12px',
            },
          }}
          slotProps={{
            inputLabel: params.slotProps?.inputLabel,
            input: {
              ...(params.slotProps?.input ?? {}),
              ...InputProps,
            },
            htmlInput: {
              ...(params.slotProps?.htmlInput ?? {}),
              ...inputProps,
              className: clsx(
                params.slotProps?.htmlInput?.className,
                inputProps?.className,
              ),
            },
          }}
        />
      )}
    />
  );
}
